// What tapping a mine chip on a planet card should do. Shared by the full
// card (PlanetDetailsDisplayDeed) and the phone compact card so the two can
// never drift apart.
//
// Buying an empty single-slot mine stays a one-tap action — there is nothing
// to choose and nothing to lose. SELLING never happens on a tap: a mistap on
// an owned mine used to refund it at 75% instantly, so every sell (and every
// multi-slot mine, as before) goes through the quantity screen where the
// player confirms.

export type MineTapIntent =
  | { action: 'buy', quantity: number }
  | { action: 'modal' }

export function resolveMineTap(current: number, max: number): MineTapIntent {
  if (max === 1 && current === 0) {
    return { action: 'buy', quantity: 1 }
  }
  return { action: 'modal' }
}

export type PurchaseButtonMode = 'sell' | 'buy' | 'finance' | 'blocked'

export interface PurchaseButtonState {
  mode: PurchaseButtonMode
  label: string
  disabled: boolean
  /** Ask the server to raise the shortfall from the player's accounts. */
  autoFinance: boolean
}

/**
 * What the confirm button on the mine/barrier quantity screen should say and
 * do. Being short of cash is not a dead end: when the accounts can cover the
 * gap the button offers to withdraw it as part of the purchase, rather than
 * greying out and leaving the player to work out what to sell.
 *
 * @param quantity      signed slot change — negative sells
 * @param cashAfter     cash left once the purchase is paid for
 * @param availableCash what could still be raised from bonds / LOC / loans
 * @param noun          what is being bought, e.g. "Mine" or "Barrier"
 */
export function resolvePurchaseButton(
  quantity: number,
  cashAfter: number,
  availableCash: number,
  noun = 'Mine',
): PurchaseButtonState {
  if (quantity === 0) {
    return { mode: 'buy', label: `Purchase ${noun}`, disabled: true, autoFinance: false }
  }

  if (quantity < 0) {
    return { mode: 'sell', label: `Sell ${noun}`, disabled: false, autoFinance: false }
  }

  if (cashAfter >= 0) {
    return { mode: 'buy', label: `Purchase ${noun}`, disabled: false, autoFinance: false }
  }

  // Short: -cashAfter is the gap. Offer the withdrawal only if the accounts
  // genuinely cover it — a button that promises money that isn't there is
  // worse than a disabled one.
  if (availableCash >= -cashAfter) {
    return { mode: 'finance', label: `Withdraw Cash & Purchase`, disabled: false, autoFinance: true }
  }

  return { mode: 'blocked', label: 'Not Enough Cash', disabled: true, autoFinance: false }
}

/**
 * Whether a mine slot is still empty — the "you haven't bought this yet"
 * state the chips render washed out.
 */
export function isMineUnpurchased(current: number): boolean {
  return !current
}

/**
 * Chip classes for a mine/barrier slot. Owned slots keep the solid resource
 * colour; empty ones get the same hue washed right out (`-bg-idle` sets the
 * hue, `is-idle` applies the theme-aware wash) so an empty slot reads as
 * empty at a glance in either theme.
 */
export function mineChipClasses(resource: string, current: number): string {
  return isMineUnpurchased(current)
    ? `${resource}-bg-idle is-idle`
    : `${resource}-bg drop-shadow`
}
